import { ref } from 'vue';
import { api } from '../services/api';
import { mediaCache } from '../services/mediaCacheService';
import { localSync } from '../services/LocalSyncService';
import { learningEngine, checkTranslationMatch } from '../services/LearningWorkflowEngine';
import { useSyncStatus } from './useSyncStatus';
import type { CardData, OutboundAction, OutboundButton, SyncWordRecord, ToastAlert } from '../types';

const activeCard = ref<CardData | null>(null);
const currentButtons = ref<OutboundButton[][]>([]);
const currentMessageText = ref<string>('');
const currentPhotoBase64 = ref<string | null>(null);
const toastAlerts = ref<ToastAlert[]>([]);
const isDrawerOpen = ref<boolean>(false);
const drawerPrompt = ref<string>('Send words or upload file:');
const isLoadingAction = ref<boolean>(false);

let syncListenerBound = false;

export function useLearningSession() {
  const { setDownloadProgress, markSyncSuccess, markSyncError } = useSyncStatus();

  const addToast = (text: string, type: ToastAlert['type'] = 'info') => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    toastAlerts.value.push({ id, text, type, timestamp: Date.now() });

    // Auto-dismiss after 6 seconds
    setTimeout(() => {
      dismissToast(id);
    }, 6000);
  };

  const dismissToast = (id: string) => {
    toastAlerts.value = toastAlerts.value.filter((t) => t.id !== id);
  };

  const isOffline = (): boolean => {
    return (typeof navigator !== 'undefined' && !navigator.onLine) || localSync.state.connection === 'offline';
  };

  const parseCardFromText = (rawText: string, customData?: Record<string, any>): CardData => {
    if (!rawText) return { word: '' };

    // 1. Unescape Telegram MarkdownV2 backslashes: e.g. \. \- \! \( \) \* \_
    const cleanText = rawText.replace(/\\([.\-!*_()[\]~`>#+=|{}])/g, '$1');

    const lines = cleanText.split('\n').map((l) => l.trim()).filter((l) => Boolean(l));
    if (lines.length === 0) {
      return { word: cleanText };
    }

    const isTranslationResult =
      cleanText.includes('Translation is Correct') ||
      cleanText.includes('Wrong translation');
    const isCorrect = isTranslationResult ? cleanText.includes('Translation is Correct') : undefined;

    const isTypingDrill =
      !isTranslationResult && (
        cleanText.includes('Please, write translation') ||
        cleanText.includes('🔽 Please, write translation 🔽')
      );

    // Check if this is an informational / menu prompt rather than a flashcard
    const isInformational =
      !isTypingDrill && !isTranslationResult && (
        cleanText.includes('Select language') ||
        cleanText.includes('There is no new words') ||
        cleanText.includes('How to add words') ||
        cleanText.includes('Please select a course') ||
        cleanText.includes('Please select category')
      );

    let word = '';
    let transcription = '';
    let translation = '';
    let isSpoiler = false;
    let isReverse = false;
    let level = customData?.stars ? Number(customData.stars) : undefined;
    const wordId = customData?.word_id ? Number(customData.word_id) : undefined;
    const dictionaryId = customData?.dictionary_id ? Number(customData.dictionary_id) : undefined;
    const examples: Array<{ original: string; translation?: string }> = [];

    if (isTypingDrill) {
      level = 5;
      const promptLines = lines.filter(
        (l) => !l.includes('Please, write translation') && !l.includes('🔽') && !l.startsWith('>')
      );
      if (promptLines.length > 0) {
        translation = promptLines[0].replace(/\*/g, '').trim();
      }
      if (customData?.word) {
        word = customData.word;
      }
    } else {
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Check for transcription line (e.g. 🗣️ [gamarjoba] or 🗣️ ||[gamarjoba]||)
        if (line.includes('🗣️')) {
          let ph = line.replace('🗣️', '').replace(/\|\|/g, '').trim();
          const m = ph.match(/^\[(.*?)\]$/);
          if (m) ph = m[1];
          transcription = ph;
          continue;
        }

        // Check for hint / quote lines: e.g. **> or >ℹ️ Press to see the hint or >line
        if (line.startsWith('>') || line.startsWith('**>')) {
          const hintContent = line.replace(/^\*{0,2}>+/, '').replace(/\ufe0f/g, '').replace(/\|\|/g, '').trim();
          if (
            hintContent &&
            !hintContent.toLowerCase().includes('press to see the hint') &&
            !hintContent.toLowerCase().includes('press to see a hint') &&
            !hintContent.startsWith('ℹ')
          ) {
            examples.push({ original: hintContent });
          }
          continue;
        }

        // First line: Word: Translation or Word
        if (!word) {
          if (line.includes(':')) {
            const colonIdx = line.indexOf(':');
            let left = line.substring(0, colonIdx).trim();
            let right = line.substring(colonIdx + 1).trim();

            if (right.includes('||')) {
              isSpoiler = true;
              right = right.replace(/\|\|/g, '').trim();
            }

            // Reverse card detection: e.g. "sun: ||*მზე*||"
            if (line.includes(': ||*') || (line.includes(':') && line.includes('||*'))) {
              isReverse = true;
              level = level || 4;
              word = right.replace(/\*/g, '').trim();
              translation = left.replace(/\*/g, '').trim();
            } else {
              left = left.replace(/\*/g, '').trim();
              right = right.replace(/\*/g, '').replace(/^[\\/\s]+/, '').trim();
              word = left;
              translation = right;
              if (isSpoiler && !level) level = 3;
            }
            continue;
          }

          const transMatch = line.match(/^(.*?)\s*\[(.*?)\]$/);
          if (transMatch) {
            word = transMatch[1].replace(/\*/g, '').trim();
            transcription = transMatch[2].trim();
            continue;
          }

          word = line.replace(/\*/g, '').replace(/\|\|/g, '').trim();
          continue;
        }

        // If word is set and translation is not set yet
        if (!translation && !line.includes('🗣️') && !line.startsWith('>')) {
          let tr = line;
          if (tr.includes('||')) {
            isSpoiler = true;
            tr = tr.replace(/\|\|/g, '').trim();
            if (!level) level = 3;
          }
          translation = tr.replace(/\*/g, '').replace(/^[\\/\s]+/, '').trim();
          continue;
        }

        // Examples / hints
        if (line.includes('—') || line.includes('-') || line.includes(':')) {
          const parts = line.split(/[—\-:]/);
          examples.push({
            original: parts[0].replace(/\*/g, '').trim(),
            translation: parts.slice(1).join('—').replace(/\*/g, '').trim(),
          });
        } else if (!line.startsWith('>')) {
          examples.push({ original: line.replace(/\*/g, '').trim() });
        }
      }
    }

    if (!level) {
      if (isTypingDrill) level = 5;
      else if (isReverse) level = 4;
      else if (isSpoiler) level = 3;
      else level = 1;
    }

    return {
      word: word || cleanText,
      transcription,
      translation,
      examples,
      isSpoiler,
      isRevealed: false,
      isInformational,
      rawText: cleanText,
      level,
      isReverse,
      isTypingDrill,
      wordId,
      dictionaryId,
      isTranslationResult,
      isCorrect,
      imageUrl: currentPhotoBase64.value ? `data:image/jpeg;base64,${currentPhotoBase64.value}` : undefined,
    };
  };

  const cacheParsedCardInLocalDb = async (card: CardData) => {
    if (!card.word || card.isInformational || card.isTranslationResult) return;
    const all = await localSync.getAllWords();
    const existing = all.find((w) => w.word === card.word || (card.wordId && w.id === card.wordId));
    if (existing) return;

    const syntheticId = card.wordId || all.reduce((max, w) => Math.max(max, w.id), 1000) + 1;
    const record: SyncWordRecord = {
      id: syntheticId,
      word_id: syntheticId,
      dictionary_id: card.dictionaryId || 1,
      language_code: 'ka',
      word: card.word,
      short_word: card.word,
      part_of_speech_id: 1,
      part_of_speech_name: card.partOfSpeech || 'Noun',
      translations: card.translation ? { en: card.translation } : {},
      translation: card.translation || '',
      phonetics_map: card.transcription ? { ipa: card.transcription } : {},
      phonetics: card.transcription || '',
      hint: (card.examples || [])
        .map((ex) => (ex.translation ? `${ex.original} — ${ex.translation}` : ex.original))
        .join('\n'),
      stars: card.level || 3,
      is_learned: false,
      postponed_date: null,
      sync_version: 1,
    };
    await localSync.upsertWordsLocal([record]);
    if (!card.wordId) {
      card.wordId = syntheticId;
    }
  };

  const applyAction = (action: OutboundAction) => {
    if (action.media_bytes_b64) {
      currentPhotoBase64.value = action.media_bytes_b64;
    }

    if (action.text) {
      const text = action.text;
      currentMessageText.value = text;

      if (
        text.includes('AI is working in the background') ||
        text.includes('🔄') ||
        text.includes('Working in the background')
      ) {
        addToast(text, 'info');
        return;
      }

      if (text.startsWith('New words:') || text.includes('words added') || text.includes('Added words:')) {
        addToast(text, 'success');
        void localSync.poll();
      }

      if (
        text.toLowerCase().includes('send me the words') ||
        text.toLowerCase().includes('enter words') ||
        text.toLowerCase().includes('send words or file')
      ) {
        drawerPrompt.value = text;
        isDrawerOpen.value = true;
      }

      const parsed = parseCardFromText(text, action.custom_data);
      activeCard.value = parsed;
      void cacheParsedCardInLocalDb(parsed);

      if (action.audio_path) {
        void mediaCache.cacheMediaUrl(action.audio_path);
      }
    }

    if (action.buttons && action.buttons.length > 0) {
      currentButtons.value = action.buttons
        .map((row) =>
          row.filter((b) => {
            const cap = (b.caption || '').replace(/\ufe0f/g, '').trim().toLowerCase();
            const cb = (b.callback_data || '').trim().toLowerCase();
            return !(
              cap.includes('info') ||
              cap.includes('инфо') ||
              cap === 'ℹ' ||
              cb === '/info' ||
              cb.includes('learn words/info') ||
              cb.includes('courses/learn words/info')
            );
          })
        )
        .filter((row) => row.length > 0);
    }
  };

  const applyActions = (actions: OutboundAction[]) => {
    for (const action of actions) {
      applyAction(action);
    }
    markSyncSuccess();
  };

  const presentNextLocalCard = async (options?: { excludeWordId?: number; deterministicFirst?: boolean }) => {
    const next = await learningEngine.selectNextCard(options);
    activeCard.value = next.card;
    currentButtons.value = next.buttons;
  };

  if (typeof window !== 'undefined' && !syncListenerBound) {
    syncListenerBound = true;
    window.addEventListener('geobu-sync-changed', async () => {
      if (!activeCard.value || activeCard.value.isInformational) {
        const words = await localSync.getAllWords();
        if (words.length > 0) {
          await presentNextLocalCard({ deterministicFirst: true });
        }
      } else if (activeCard.value.wordId) {
        // Refresh current card with updated preferences or stars from 3-way merge
        const [word, prefs] = await Promise.all([
          localSync.getWord(activeCard.value.wordId),
          localSync.getPreferences(),
        ]);
        if (word && !word.is_learned && word.stars > 0) {
          const updatedCard = learningEngine.buildCardFromWord(word, prefs);
          updatedCard.isRevealed = activeCard.value.isRevealed;
          activeCard.value = updatedCard;
          currentButtons.value = learningEngine.buildButtonsForWord(word, prefs);
        }
      }
    });
  }

  const initSession = async () => {
    isLoadingAction.value = true;
    try {
      const savedUser = api.getSavedUser();
      const userId = savedUser?.user_id || 'anonymous';
      await localSync.start(userId);

      // 1. If we already have words in IndexedDB, immediately present a card offline-first
      const localWords = await localSync.getAllWords();
      if (localWords.length > 0 && !activeCard.value) {
        await presentNextLocalCard({ deterministicFirst: true });
      }

      // 2. If online, synchronize session state & connect SSE stream
      if (!isOffline()) {
        const state = await api.getSessionState();
        if (state.user) {
          api.saveUser(state.user);
          await localSync.start(state.user.user_id);
        }
        if (state.actions && state.actions.length > 0) {
          applyActions(state.actions);
        } else if (!activeCard.value) {
          await presentNextLocalCard({ deterministicFirst: true });
        }

        api.connectEventStream(
          (action) => {
            applyAction(action);
          },
          () => {
            localSync.state.realtime = 'connected';
          }
        );
      } else if (!activeCard.value) {
        await presentNextLocalCard({ deterministicFirst: true });
      }
    } catch (err: any) {
      console.warn('Session init offline or error:', err);
      const localWords = await localSync.getAllWords();
      if (localWords.length > 0) {
        await presentNextLocalCard({ deterministicFirst: true });
      } else {
        markSyncError(err.message || 'Connection error');
      }
    } finally {
      isLoadingAction.value = false;
    }
  };

  const handleButtonClick = async (btn: OutboundButton) => {
    if (!btn.callback_data && !btn.caption) return;
    const cb = (btn.callback_data || '').trim().toLowerCase();
    const cap = (btn.caption || '').trim().toLowerCase();

    // 1. Remote-only LLM action: Add Words
    if (
      cb.includes('add_words') ||
      cap.includes('add word') ||
      cap.includes('добавить слова')
    ) {
      if (isOffline()) {
        const errMsg = 'Cannot add new words while offline: AI word processing requires an internet connection.';
        addToast(errMsg, 'error');
        markSyncError(errMsg);
        return;
      }
      isDrawerOpen.value = true;
      // Notify server state machine non-blockingly if online
      void api.sendEvent({
        event_type: 'button',
        callback_data: btn.callback_data || btn.caption,
      }).catch(() => null);
      return;
    }

    // 2. Dictionary / Preferences modal shortcut
    if (cb.includes('open_dict') || cap.includes('dictionary')) {
      return;
    }

    // 3. Language rotation in JS
    if (cb === 'switch_lang' || cap.includes('next lang')) {
      const nextLang = await learningEngine.switchNextLanguage();
      addToast(`Switched study deck to ${nextLang.toUpperCase()}`, 'info');
      await presentNextLocalCard({ deterministicFirst: true });
      return;
    }

    // 4. Local JS Study Workflow (Next, Skip)
    isLoadingAction.value = true;
    try {
      const currentWordId = activeCard.value?.wordId;
      const localWords = await localSync.getAllWords();
      const eligibleWords = learningEngine.getActiveWindowWords(localWords);

      if (isOffline()) {
        // 100% Offline: select next card directly from IndexedDB via LearningWorkflowEngine
        await presentNextLocalCard({ excludeWordId: currentWordId });
        return;
      }

      // Online: advance immediately from IndexedDB if multiple words are loaded,
      // while also dispatching to /api/web/events for compatibility with server-driven flows & mocks
      if (eligibleWords.length > 1) {
        await presentNextLocalCard({ excludeWordId: currentWordId });
        void api
          .sendEvent({
            event_type: btn.callback_data?.startsWith('/') ? 'command' : 'button',
            command: btn.callback_data?.startsWith('/') ? btn.callback_data : undefined,
            callback_data: !btn.callback_data?.startsWith('/') ? btn.callback_data || btn.caption : undefined,
          })
          .catch(() => null);
      } else {
        const res = await api.sendEvent(
          btn.callback_data?.startsWith('/')
            ? { event_type: 'command', command: btn.callback_data }
            : { event_type: 'button', callback_data: btn.callback_data || btn.caption }
        );
        if (res.actions && res.actions.length > 0) {
          applyActions(res.actions);
        } else {
          await presentNextLocalCard({ excludeWordId: currentWordId });
        }
      }
    } catch (err: any) {
      // If network dropped mid-click, fall back to offline JS workflow seamlessly
      const localWords = await localSync.getAllWords();
      if (localWords.length > 0) {
        await presentNextLocalCard({ excludeWordId: activeCard.value?.wordId });
      } else {
        addToast(err.message || 'Action failed', 'error');
        markSyncError(err.message || 'Action error');
      }
    } finally {
      isLoadingAction.value = false;
    }
  };

  const handleTextSubmit = async (text: string) => {
    if (!text.trim()) return;

    // Case 1: Adding new words via ContextualInputDrawer (Requires LLM / Internet!)
    if (isDrawerOpen.value) {
      if (isOffline()) {
        const errMsg = 'Cannot add new words while offline: AI word extraction and translation require an internet connection.';
        addToast(errMsg, 'error');
        markSyncError(errMsg);
        return;
      }

      isLoadingAction.value = true;
      try {
        const res = await api.sendEvent({
          event_type: 'reply',
          text: text.trim(),
        });
        isDrawerOpen.value = false;
        if (res.actions) {
          applyActions(res.actions);
        }
        void localSync.poll();
      } catch (err: any) {
        const errMsg = err.message || 'Failed to submit words to AI service';
        addToast(errMsg, 'error');
        markSyncError(errMsg);
      } finally {
        isLoadingAction.value = false;
      }
      return;
    }

    // Case 2: In-study translation check (Level 5 Writing Drill or free-text answer) -> Runs in JS!
    if (activeCard.value) {
      const expected =
        (activeCard.value.level ?? 3) >= 4
          ? activeCard.value.word
          : activeCard.value.translation || activeCard.value.word;
      const isCorrect = checkTranslationMatch(text.trim(), expected);
      if (activeCard.value.wordId && isCorrect) {
        void learningEngine.reactToWord(activeCard.value.wordId, 'star_up', activeCard.value);
      }
    }

    if (!isOffline()) {
      void api
        .sendEvent({
          event_type: 'reply',
          text: text.trim(),
        })
        .catch(() => null);
    }
  };

  const handleFileUpload = async (file: File) => {
    // File & Image OCR upload strictly requires remote LLM / backend processing
    if (isOffline()) {
      const errMsg = 'Cannot upload files while offline: AI OCR and vocabulary extraction require an internet connection.';
      addToast(errMsg, 'error');
      markSyncError(errMsg);
      return;
    }

    isLoadingAction.value = true;
    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1] || result;
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const base64Content = await base64Promise;

      const res = await api.sendEvent({
        event_type: 'file',
        file: {
          file_name: file.name,
          mime_type: file.type || 'application/octet-stream',
          size: file.size,
          data: base64Content,
        },
        caption: file.name,
      });

      isDrawerOpen.value = false;
      if (res.actions) {
        applyActions(res.actions);
      }
      void localSync.poll();
    } catch (err: any) {
      const errMsg = err.message || 'Failed to upload file: internet connection required';
      addToast(errMsg, 'error');
      markSyncError(errMsg);
    } finally {
      isLoadingAction.value = false;
    }
  };

  return {
    activeCard,
    currentButtons,
    currentMessageText,
    toastAlerts,
    isDrawerOpen,
    drawerPrompt,
    isLoadingAction,
    initSession,
     presentNextLocalCard,
    handleButtonClick,
    handleTextSubmit,
    handleFileUpload,
    dismissToast,
    addToast,
    setDownloadProgress,
  };
}
